import styles from "./SubscribeCTA.module.css";
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useId } from "react";
import { createPortal } from "react-dom";
import ConnectSplash from "../ConnectSplash/ConnectSplash";
import { usePreviewStores } from "../../data/CmsPreviewContext";
import {
  isValidEmailAddress,
  KIT_SUPPORT_EMAIL,
  KIT_SUBSCRIBE_URL,
  subscribeToKit,
} from "../../data/kitSubscribe";
import { resolveJoinInvite } from "../../data/joinInviteConfig";
import { rememberJoinInviteDismissed } from "../../data/joinInviteStorage";
import { linkifyEmails } from "../../data/linkifyEmails";

const SubscribeCTA = (props) => {
  const { content, catalog } = usePreviewStores();
  const joinInvite = useMemo(
    () => resolveJoinInvite(content, catalog),
    [content, catalog],
  );
  const [emailAddress, setEmailAddress] = useState("");
  const [modal, setModal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const revealOnClick = Boolean(props.revealOnClick);
  const [emailRevealed, setEmailRevealed] = useState(!revealOnClick);
  const emailFieldId = useId();
  const legalId = useId();
  const emailInputRef = useRef(null);
  const submitRef = useRef(null);
  const rootRef = useRef(null);
  const legalRef = useRef(null);
  const compactLegal = props.layout === "compact" || revealOnClick;
  const showLegal = Boolean(
    joinInvite.showLegal && String(joinInvite.legal || "").trim(),
  );
  const legalVisible = showLegal && (!revealOnClick || emailRevealed);
  const [legalFloatStyle, setLegalFloatStyle] = useState(null);
  const submitLabel =
    props.submitLabel != null && props.submitLabel !== ""
      ? props.submitLabel
      : "Join";
  const emailInput = (e) => {
    setEmailAddress(e.target.value);
  };

  // Focus the field once the nav “Join” control expands it.
  useEffect(() => {
    if (!revealOnClick || !emailRevealed) return;
    const id = window.requestAnimationFrame(() => {
      emailInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [revealOnClick, emailRevealed]);

  // Collapse again when clicking outside or pressing Escape (nav expand mode only).
  useEffect(() => {
    if (!revealOnClick || !emailRevealed || modal || isSubmitting) return;

    const onPointerDown = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (legalRef.current?.contains(t)) return;
      setEmailRevealed(false);
    };
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setEmailRevealed(false);
      submitRef.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [revealOnClick, emailRevealed, modal, isSubmitting]);

  // Compact Join lives in a backdrop-filtered bar; portal consent so it is
  // not clipped. Hang below when there is room, above for the bottom dock.
  useLayoutEffect(() => {
    if (!compactLegal || !legalVisible) {
      setLegalFloatStyle(null);
      return undefined;
    }

    const place = () => {
      const root = rootRef.current;
      if (!root) return;
      const r = root.getBoundingClientRect();
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.min(Math.max(r.width, 320), vw - margin * 2);
      let left = r.left + r.width / 2 - width / 2;
      left = Math.max(margin, Math.min(left, vw - width - margin));
      const hangUp = r.top > vh - r.bottom;
      setLegalFloatStyle({
        position: "fixed",
        left: `${left}px`,
        width: `${width}px`,
        top: hangUp ? "auto" : `${r.bottom + 8}px`,
        bottom: hangUp ? `${Math.max(margin, vh - r.top + 8)}px` : "auto",
        visibility: "visible",
      });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", place);
    vv?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      vv?.removeEventListener("resize", place);
      vv?.removeEventListener("scroll", place);
    };
  }, [compactLegal, legalVisible, joinInvite.legal]);

  const closeModal = () => {
    setModal(null);
  };

  useEffect(() => {
    if (!modal) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setModal(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [modal]);

  const sendEmail = async (e) => {
    e.preventDefault();
    // First click on nav Join only reveals the field; second submits.
    if (revealOnClick && !emailRevealed) {
      setEmailRevealed(true);
      return;
    }
    if (isSubmitting) return;

    const cleanEmailAddress = emailAddress.replaceAll(" ", "");
    if (!isValidEmailAddress(cleanEmailAddress)) {
      setModal({
        type: "error",
        title: "Check that email",
        message: `"${cleanEmailAddress}" does not appear to be a valid email address. Please fix it and try again.`,
        showSupport: false,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await subscribeToKit(cleanEmailAddress);
      rememberJoinInviteDismissed(joinInvite.hideDays);
      setEmailAddress("");
      if (revealOnClick) setEmailRevealed(false);
      setModal({
        type: "success",
        email: cleanEmailAddress,
      });
    } catch (error) {
      setModal({
        type: "error",
        title: "Could not join",
        message:
          error?.message ||
          `We could not add that email. Please try again, or write ${KIT_SUPPORT_EMAIL}.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const layoutClass =
    (props.layout === "compact" ? " " + styles["subscribeCTA-compact"] : "") +
    (revealOnClick ? " " + styles["subscribeCTA-reveal"] : "") +
    (revealOnClick && emailRevealed
      ? " " + styles["subscribeCTA-email-open"]
      : "") +
    (revealOnClick && !emailRevealed
      ? " " + styles["subscribeCTA-email-closed"]
      : "");

  const legalNode =
    showLegal && (!compactLegal || legalVisible) ? (
      <div
        ref={compactLegal ? legalRef : undefined}
        id={legalId}
        className={
          styles["subscribeCTA-legal"] +
          (compactLegal ? " " + styles["subscribeCTA-legal-float"] : "")
        }
        style={
          compactLegal
            ? legalFloatStyle || { position: "fixed", visibility: "hidden" }
            : undefined
        }
        data-subscribe-legal={compactLegal ? "float" : "inline"}
      >
        {linkifyEmails(joinInvite.legal)}
      </div>
    ) : null;

  return (
    <div
      ref={rootRef}
      className={styles["subscribeCTA-container"] + layoutClass}
      data-email-open={
        revealOnClick ? (emailRevealed ? "true" : "false") : undefined
      }
    >
      <form
        className={styles["subscribeCTA-form"]}
        action={KIT_SUBSCRIBE_URL}
        method="post"
        onSubmit={sendEmail}
        noValidate
        aria-busy={isSubmitting ? "true" : undefined}
      >
        <p>Stay connected</p>
        <span className={styles["subscribe-input-container"]}>
          <input
            ref={emailInputRef}
            id={emailFieldId}
            type="email"
            name="email_address"
            autoComplete="email"
            inputMode="email"
            className={styles["subscribeCTA-email-input"]}
            placeholder="Email address"
            aria-label="Email address"
            aria-describedby={legalVisible ? legalId : undefined}
            aria-hidden={revealOnClick && !emailRevealed ? true : undefined}
            tabIndex={revealOnClick && !emailRevealed ? -1 : undefined}
            value={emailAddress}
            onChange={emailInput}
            disabled={isSubmitting}
          />
          <input
            ref={submitRef}
            type="submit"
            className={
              styles["subscribeCTA-email-submit"] +
              " " +
              (emailAddress ? styles["active-submit"] : "")
            }
            value={submitLabel}
            disabled={isSubmitting}
            aria-expanded={
              revealOnClick ? (emailRevealed ? "true" : "false") : undefined
            }
            aria-controls={revealOnClick ? emailFieldId : undefined}
          />
        </span>
      </form>
      {compactLegal && legalNode && typeof document !== "undefined"
        ? createPortal(legalNode, document.body)
        : legalNode}
      {modal?.type === "success" &&
        typeof document !== "undefined" &&
        createPortal(
          <ConnectSplash
            variant="pending"
            email={modal.email}
            onDismiss={closeModal}
          />,
          document.body,
        )}
      {modal?.type === "error" &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={styles["confirm-modal"]}
            role="dialog"
            aria-modal="true"
            aria-labelledby="subscribe-cta-modal-title"
            data-subscribe-cta-modal={modal.type}
          >
            <div className={styles.background}></div>
            <div className={styles.content}>
              <h3 id="subscribe-cta-modal-title" className="sub-title">
                {modal.title}
              </h3>
              <p>{modal.message}</p>
              {modal.showSupport !== false && (
                <p>
                  If it still will not work, write {KIT_SUPPORT_EMAIL} and we
                  will take care of the rest.
                </p>
              )}
              <div className={styles["button-container"]}>
                <button
                  type="button"
                  className={styles["button"] + " " + styles["ok-button"]}
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SubscribeCTA;
